import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";

import { HERO_CHART_HEIGHT } from "../constants";
import { Wrapper } from "../styles";
import {
  ChartArea,
  Headline,
  HeroCard,
  SubStat,
  SubStats,
  TopBar,
} from "./InvestmentsHero/styles";
import { LeftGroup, Wrapper as FiltersBar } from "./InvestmentsFilters/styles";
import {
  Body as InsightsBody,
  Column,
  ColumnHeader,
  Header as InsightsHeader,
  Name,
  Panel,
  Row,
  RowList,
  Track,
  Value,
} from "./InvestmentsInsights/styles";
import {
  BodyRow,
  Card,
  CardList,
  CardTop,
  CardTrendBox,
  CardValueRow,
  Cell,
  HeaderCell,
  HeaderRow,
  Section,
  Table,
  TableCard,
  TableScroll,
  TrendBox,
  TrendCell,
  TypeRow,
} from "./InvestmentsHoldings/styles";
import { SkeletonBox } from "./styles";

const INSIGHT_COLUMNS = [0, 1];
const INSIGHT_ROWS = [0, 1, 2];
const HOLDING_ROWS = [0, 1, 2, 3, 4];
const NUMERIC_CELLS = [0, 1, 2, 3];

// Reuses each real section's own styled components, so a structural rename breaks
// this file at compile time instead of letting the skeleton silently drift from the page.
export const InvestmentsSkeleton: React.FC = () => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const filtersVariant = isCompact ? "standalone" : "inline";

  const heroChartHeight = isCompact
    ? `${HERO_CHART_HEIGHT.compact}px`
    : `${HERO_CHART_HEIGHT.regular}px`;
  const hero = (
    <HeroCard>
      <TopBar>
        <SkeletonBox width="150px" height="16px" />
        {isCompact ? null : <SkeletonBox width="190px" height="44px" />}
      </TopBar>
      <Headline>
        <SkeletonBox width="200px" height="40px" />
        <SkeletonBox width="150px" height="22px" />
      </Headline>
      <ChartArea>
        <SkeletonBox height={heroChartHeight} />
      </ChartArea>
      <SubStats>
        <SubStat>
          <SkeletonBox width="70px" height="16px" />
          <SkeletonBox width="80px" height="16px" />
        </SubStat>
        <SubStat>
          <SkeletonBox width="70px" height="16px" />
          <SkeletonBox width="32px" height="16px" />
        </SubStat>
      </SubStats>
    </HeroCard>
  );

  const filters = (
    <FiltersBar $variant={filtersVariant}>
      <LeftGroup $variant={filtersVariant}>
        <SkeletonBox width="120px" height="40px" />
        <SkeletonBox width="110px" height="28px" />
      </LeftGroup>
      {!isCompact && <SkeletonBox width="90px" height="18px" />}
    </FiltersBar>
  );

  const insights = (
    <Panel>
      <InsightsHeader as="div">
        <SkeletonBox width="20px" height="20px" />
        <SkeletonBox width="80px" height="18px" />
        <SkeletonBox width="140px" height="16px" />
      </InsightsHeader>
      <InsightsBody>
        {INSIGHT_COLUMNS.map((column) => (
          <Column key={column}>
            <ColumnHeader>
              <SkeletonBox width="110px" height="20px" />
              <SkeletonBox width="220px" height="14px" />
            </ColumnHeader>
            <RowList>
              {INSIGHT_ROWS.map((row) => (
                <Row key={row}>
                  <Name as="div">
                    <SkeletonBox width="90px" height="16px" />
                  </Name>
                  <Track>
                    <SkeletonBox height="10px" />
                  </Track>
                  <Value as="div">
                    <SkeletonBox width="48px" height="16px" />
                  </Value>
                </Row>
              ))}
            </RowList>
          </Column>
        ))}
      </InsightsBody>
    </Panel>
  );

  const holdingsTable = (
    <TableCard>
      <TableScroll>
        <Table>
          <thead>
            <HeaderRow>
              <HeaderCell $align="left">
                <SkeletonBox width="100px" height="14px" />
              </HeaderCell>
              <HeaderCell $align="left">
                <SkeletonBox width="60px" height="14px" />
              </HeaderCell>
              {NUMERIC_CELLS.map((cell) => (
                <HeaderCell key={cell} $align="right">
                  <SkeletonBox width="60px" height="14px" />
                </HeaderCell>
              ))}
              <HeaderCell $align="right">
                <SkeletonBox width="60px" height="14px" />
              </HeaderCell>
            </HeaderRow>
          </thead>
          <tbody>
            {HOLDING_ROWS.map((row) => (
              <BodyRow key={row}>
                <Cell $align="left">
                  <SkeletonBox width="140px" height="16px" />
                  <TypeRow>
                    <SkeletonBox width="90px" height="12px" />
                  </TypeRow>
                </Cell>
                <Cell $align="left">
                  <SkeletonBox width="70px" height="14px" />
                </Cell>
                {NUMERIC_CELLS.map((cell) => (
                  <Cell key={cell} $align="right">
                    <SkeletonBox width="80px" height="16px" />
                  </Cell>
                ))}
                <TrendCell>
                  <TrendBox>
                    <SkeletonBox height="36px" />
                  </TrendBox>
                </TrendCell>
              </BodyRow>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    </TableCard>
  );

  const holdingsCards = (
    <CardList>
      {HOLDING_ROWS.map((row) => (
        <Card key={row}>
          <CardTop>
            <SkeletonBox width="140px" height="18px" />
            <SkeletonBox width="64px" height="24px" />
          </CardTop>
          <TypeRow>
            <SkeletonBox width="100px" height="14px" />
          </TypeRow>
          <CardValueRow>
            <SkeletonBox width="120px" height="24px" />
            <SkeletonBox width="80px" height="16px" />
          </CardValueRow>
          <CardTrendBox>
            <SkeletonBox height="64px" />
          </CardTrendBox>
          <SkeletonBox width="90px" height="12px" />
        </Card>
      ))}
    </CardList>
  );

  const holdingsContent = isCompact ? holdingsCards : holdingsTable;

  return (
    <Wrapper aria-busy="true" aria-label="Loading investments">
      {hero}
      {filters}
      {insights}
      <Section>{holdingsContent}</Section>
    </Wrapper>
  );
};
